import { getVehicles } from '@data/selectors';
import { getDateRange } from '@features/ui/selectors';
import Toolbar from '@features/ui/Toolbar';
import { Dispatch } from '@reduxjs/toolkit';
import { AutoSuggestSuggestion } from '@rio-cloud/rio-uikit/AutoSuggest';
import EmptyState from '@rio-cloud/rio-uikit/EmptyState';
import { FormattedMessage, injectIntl, IntlFormatters } from 'react-intl';
import { connect } from 'react-redux';
import { compose } from 'redux';

import { State } from '../../setup/types';
import { DateRange } from '../../types';
import { setSelection, startDownload } from './actions';
import DataLoader from './DataLoader';
import { DRIVER, getSelection, getStatus, getSuggestions, VEHICLE } from './selectors';
import Suggest from './Suggest';
import TableElement from './TableElement';
import { CoDriverDownloadPayload, Status } from './types';

export const CoDriver: React.FC<{
    intl: IntlFormatters;
    suggestions: AutoSuggestSuggestion[];
    setSelection: (suggestions?: AutoSuggestSuggestion) => void;
    selection?: AutoSuggestSuggestion;
    startDownload: (payload: CoDriverDownloadPayload) => void;
    status: Record<string, Status>;
    dateRange: DateRange;
    allVehicles: string[];
}> = ({ intl, suggestions, setSelection, selection, startDownload, status, dateRange, allVehicles }) => {
    const placeholderMessage = intl.formatMessage({ id: 'selectVehicleOrDriver' });

    let vehicles;
    if (selection?.type === VEHICLE) {
        vehicles = [selection.id as string];
    } else {
        vehicles = allVehicles;
    }
    const drivers = selection?.type === DRIVER ? [selection.id as string] : [];

    const removeDuplicateLabels = (suggestions: AutoSuggestSuggestion[]) => {
        const seenLabels = new Set<string>();

        return suggestions.filter(suggestion => {
            const label = suggestion.label?.trim().toLowerCase();

            if (!label) return false;

            if (seenLabels.has(label)) return false;

            seenLabels.add(label);
            return true;
        });
    };
    const uniqueSuggestions = removeDuplicateLabels(suggestions);

    return (
        <div data-test="CoDriver">
            <Toolbar showLabel={true}>
                <div className="display-flex flex-column margin-top-auto margin-bottom-15">
                    <label className="table-toolbar-label">
                        <FormattedMessage id="vehicleOrDriver" />
                    </label>
                    <Suggest
                        suggestions={uniqueSuggestions}
                        placeholderMessage={placeholderMessage}
                        setSelection={setSelection}
                    />
                </div>
            </Toolbar>
            {selection?.type ? (
                <DataLoader
                    vehicles={vehicles}
                    drivers={drivers}
                    dateRange={dateRange}
                    selection={selection}
                    startDownload={startDownload}
                    status={status}
                >
                    <TableElement
                        startDownload={startDownload}
                        selection={selection}
                        dateRange={dateRange}
                        status={status}
                    />
                </DataLoader>
            ) : (
                <div>
                    <EmptyState headline={<FormattedMessage id="subModuleName.coDriver" />} />
                </div>
            )}
        </div>
    );
};

export const mapStateToProps = (state: State) => {
    return {
        suggestions: getSuggestions(state),
        selection: getSelection(state),
        status: getStatus(state),
        dateRange: getDateRange(state),
        allVehicles: Object.values(getVehicles(state)).map(vehicle => vehicle.vehicleId),
    };
};

export const mapDispatchToProps = (dispatch: Dispatch) => {
    return {
        setSelection: (selection: AutoSuggestSuggestion) => dispatch(setSelection(selection)),
        startDownload: (params: CoDriverDownloadPayload) => dispatch(startDownload(params)),
    };
};

export default compose<React.FC>(injectIntl, connect(mapStateToProps, mapDispatchToProps))(CoDriver);
